import { useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useTranslator } from '@jutro/locale';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { DateUtil } from '@xengage/gw-portals-util-js';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { Claim, Contact } from 'gw-capability-fnol-common-react';
import { WizardPageTemplate } from 'gw-portals-wizard-components-ui';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import messages from '../../FNOLWC.messages';
import metadata from './WhatPage.metadata.json5';

function FNOLWCWhatPage(props) {
    const { showAlert } = useModal();

    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        wizardData: claimVM,
        updateWizardData,
        authHeader,
        history: {
            location: { state },
        },
    } = props;
    const translator = useTranslator();
    const { FNOLService } = useDependencies('FNOLService');
    const [isLoading, setIsLoading] = useState(false);
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation,
    } = useValidation('FNOLWCWhatPage');

    useEffect(
        () => {
            const lobExtension = claimVM.lobs.workersComp;
            const { injuryIncident } = lobExtension.value;

            _.set(
                lobExtension,
                'dateReportedToEmployer',
                _.get(lobExtension, 'dateReportedToEmployer.value') ||
                    DateUtil.currentDateAtMidnight()
            );

            if (!injuryIncident.injured) {
                injuryIncident.injured = new Contact({
                    displayName: 'New person',
                    gender: null,
                    dateOfBirth: null,
                });
            }

            if (!injuryIncident.injured.primaryAddress) {
                injuryIncident.injured.initializePrimaryAddress();
            }
            updateWizardData(claimVM);
        },
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const onNext = useCallback(() => {
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (!isClaimNumberAvailable) {
            setIsLoading(true);
            return FNOLService.getFNOLDetails(
                policyNumber,
                claimVM.value,
                authHeader
            )
                .then(response => {
                    claimVM.value = new Claim(response);
                    return claimVM;
                })
                .catch(() => {
                    showAlert({
                        title: messages.wcCreateDraftErrorTitle,
                        message: messages.wcCreateDraftErrorMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                    return false;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        return claimVM;
    }, [FNOLService, authHeader, claimVM]);

    const overrides = {
        '@field': {
            showOptional: true,
            labelPosition: breakpoint === 'tablet' ? 'top' : 'left',
            phoneWide: {
                labelPosition: 'top',
            },
        },
        whatPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(messages.wcCreateDraftClaim),
        },
        whatPageContainer: {
            visible: !isLoading,
        },
    };

    const { claimStatus } = state || {};

    const validateClaimStatus = useCallback(() => {
        return (
            !_.isNil(claimStatus) &&
            claimStatus ===
                translator({
                    id: 'typekey.ClaimState.draft',
                    defaultMessage: 'Draft',
                })
        );
    }, [claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [registerInitialComponentValidation, validateClaimStatus]);

    return (
        <WizardPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            onNext={onNext}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

FNOLWCWhatPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLWCWhatPage));
